/* ==========================================================================
   Recent Products Component
   Task F42: Recently prescribed products for quick re-selection
   ========================================================================== */

import { useState, useEffect } from 'react'
import { Clock } from 'lucide-react'
import { getRecentProducts } from '@/api/portal'
import { usePortalSlug } from '@/context/PortalContext'
import { Skeleton } from '@/components/ui/Skeleton'
import type { CatalogProductForPortal } from '@/api/types'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

export interface RecentProductsProps {
  /** Callback when a recent product is selected */
  onSelect: (product: CatalogProductForPortal) => void
  /** Hide the component (e.g., after a product is selected) */
  hidden?: boolean
  /** Additional CSS classes */
  className?: string
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

/**
 * Displays recently prescribed products as clickable pills.
 * Fetches recent products on mount and renders up to 5.
 * Hidden when there are no recent products or when `hidden` is true.
 */
export function RecentProducts({
  onSelect,
  hidden = false,
  className = '',
}: RecentProductsProps) {
  const slug = usePortalSlug()
  const [products, setProducts] = useState<CatalogProductForPortal[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function fetchRecent() {
      try {
        const recent = await getRecentProducts(slug)
        if (!cancelled) {
          setProducts(recent)
        }
      } catch {
        // Silently fail — recent products is a convenience feature
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchRecent()

    return () => {
      cancelled = true
    }
  }, [slug])

  // Don't render if hidden or no products after loading
  if (hidden) return null

  if (loading) {
    return (
      <div className={`mb-4 ${className}`}>
        <div className="flex items-center gap-1.5 mb-2 text-sm text-[var(--color-gray-500)]">
          <Clock className="h-3.5 w-3.5" />
          <span>Recently prescribed</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} width="w-32" height="h-8" className="rounded-full" />
          ))}
        </div>
      </div>
    )
  }

  if (products.length === 0) return null

  return (
    <div className={`mb-4 ${className}`}>
      <div className="flex items-center gap-1.5 mb-2 text-sm text-[var(--color-gray-500)]">
        <Clock className="h-3.5 w-3.5" />
        <span>Recently prescribed</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {products.map((product) => (
          <button
            key={product.id}
            type="button"
            onClick={() => onSelect(product)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full
              border border-[var(--color-gray-300)] bg-white
              hover:border-[var(--color-primary)] hover:bg-[var(--color-primary-light,var(--color-gray-50))]
              transition-colors cursor-pointer"
          >
            <span className="font-medium text-[var(--color-gray-900)]">
              {product.display_name}
            </span>
            <span className="text-[var(--color-gray-400)]">
              {product.product_code}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

export default RecentProducts
